/* eslint-disable i18next/no-literal-string */
import { Box } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteForbidden,
    getRouteReportsSavingsSummary,
    getRouteReportsSavingsSummaryCategory,
    getRouteReportsSavingsSummaryProduct,
} from 'shared/const/router';
import { clampToZero } from 'shared/lib/number/number';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import {
    ReportDrillInMetrics,
    ReportDrillInSections,
} from 'shared/ui/ReportDrillInLayout';
import { ReportMetric } from 'shared/ui/ReportMetric/ReportMetric';
import { Page } from 'widgets/Page';
import { ProductDetailsDrawer } from 'widgets/ProductDetailsDrawer';
import { SavingsSummaryCategoriesSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryCategoriesSection';
import { SavingsSummaryProductsSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductsSection';
import { SavingsSummaryTotalSavingsMetric } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryTotalSavingsMetric';

import { useReportsPageSavingsSummaryProperty } from '../model/hooks/useReportsPageSavingsSummaryProperty';

import type { ProductMetricsDto } from 'entities/ReportSavingsSummary';
import type { ReportWidgetInteractionPayload } from 'widgets/ComposableReportWidget';

const ReportsPageSavingsSummaryProperty = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const {
        isSavingsSummaryVisible,
        propertyId,
        propertyName,
        loading,
        summary,
        byCategory,
        products,
        totalSaved,
        savingsPerUnit,
        savingsPerUnitPerMonth,
        orderCount,
        savingsPerOrder,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
    } = useReportsPageSavingsSummaryProperty();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const handleProductRowClick = useCallback(
        (sku: string, product: ProductMetricsDto) => {
            navigate(getRouteReportsSavingsSummaryProduct(sku), {
                state: {
                    product,
                    fromMissedSavings: false,
                },
            });
        },
        [navigate],
    );

    const handleCategorySelect = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const meta = payload.item.meta as {
                categoryId?: string;
                categoryTitle?: string;
            };
            const categoryId = meta.categoryId;

            if (!categoryId) {
                return;
            }

            navigate(getRouteReportsSavingsSummaryCategory(categoryId), {
                state: {
                    categoryTitle: meta.categoryTitle ?? payload.item.label,
                    contextTotalSavings: summary?.savingsAmount,
                },
            });
        },
        [navigate, summary?.savingsAmount],
    );

    const openProductDetails = useCallback((sku: string) => {
        setSelectedProductSku(sku);
    }, []);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    if (isSavingsSummaryVisible === undefined) {
        return null;
    }

    if (!isSavingsSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!propertyId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    const clampedSavingsPerUnit =
        savingsPerUnit == null ? null : clampToZero(savingsPerUnit);
    const clampedSavingsPerUnitPerMonth =
        savingsPerUnitPerMonth == null
            ? null
            : clampToZero(savingsPerUnitPerMonth);

    return (
        <Page
            dataTestId="reports-page-savings-summary-property"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={propertyName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('Savings Summary')}
                    backButtonId="back-to-savings-summary-button"
                />

                <ReportDrillInMetrics>
                    <SavingsSummaryTotalSavingsMetric
                        summary={summary ?? null}
                        primaryAmount={totalSaved}
                        loading={loading}
                    />
                    <ReportMetric
                        title={t('savingsSummary.savingsPerUnit')}
                        primary={
                            clampedSavingsPerUnit == null
                                ? '-'
                                : formatReportCurrency(clampedSavingsPerUnit)
                        }
                        primaryColor="metrics.positive"
                        secondary={
                            clampedSavingsPerUnitPerMonth === null
                                ? '-'
                                : `${formatReportCurrency(
                                      clampedSavingsPerUnitPerMonth,
                                  )} ${t(
                                      'savingsSummary.perUnitPerMonthSuffix',
                                  )}`
                        }
                        loading={loading}
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.propertyDrillInSavingsPerOrder',
                        )}
                        primary={
                            savingsPerOrder == null
                                ? '-'
                                : formatReportCurrency(savingsPerOrder)
                        }
                        primaryColor="metrics.positive"
                        loading={loading}
                    />
                    <ReportMetric
                        title={t('Orders')}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                    />
                </ReportDrillInMetrics>

                <ReportDrillInSections>
                    <SavingsSummaryCategoriesSection
                        byCategory={byCategory}
                        loading={loading}
                        onCategorySelect={handleCategorySelect}
                        tableOnly
                        tableColumnIds={[
                            'category',
                            'orders',
                            'avgSupplierSpend',
                            'totalSpend',
                            'savings',
                            'savingsPercentage',
                        ]}
                        showBottomBorder={false}
                    />
                    <SavingsSummaryProductsSection
                        products={products}
                        totalSavingsAmount={summary?.savingsAmount}
                        loading={loading}
                        onProductDetailsClick={openProductDetails}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'quantity',
                            'avgSupplierPrice',
                            'pricePaid',
                            'totalSaved',
                            'percentSaved',
                        ]}
                        showBottomBorder={false}
                    />
                    <ProductDetailsDrawer
                        sku={selectedProductSku}
                        open={Boolean(selectedProductSku)}
                        onClose={closeProductDetails}
                    />
                </ReportDrillInSections>
            </Box>
        </Page>
    );
};

export default ReportsPageSavingsSummaryProperty;
